/**
 * Service catalogue — the single source of truth for /services and every
 * /services/[slug] page.
 *
 * Why this exists: before this file the whole site was four indexable URLs
 * (/, /blog, /privacy-policy, /terms-of-service). There was no page whose
 * subject was "Google Ads management" or "SEO", so there was nothing for a
 * search engine to rank against those queries no matter how the homepage was
 * worded. Each entry below becomes a real, substantial, individually
 * indexable page with its own title, H1, body copy, FAQs and Service schema.
 *
 * Content rules for anything added here:
 *  - Every claim must be true. No invented client names, counts or results.
 *  - `answer` fields are written as self-contained 40-60 word responses,
 *    because that is the form answer engines and AI Overviews extract.
 *  - `h1` leads with the phrase a buyer would actually type.
 */

export interface ServiceFAQ {
  question: string;
  answer: string;
}

export interface ServiceSection {
  heading: string;
  body: string[];
  bullets?: string[];
}

export interface ServiceDefinition {
  slug: string;
  /** Short label for nav and breadcrumbs. */
  navLabel: string;
  /** <title> — kept under ~60 characters where possible. */
  metaTitle: string;
  metaDescription: string;
  /** On-page H1. Leads with the buying phrase. */
  h1: string;
  /** The 40-60 word direct answer that opens the page. Extraction target. */
  directAnswer: string;
  /** One-line summary used on the /services index card. */
  cardSummary: string;
  /** schema.org serviceType value. */
  serviceType: string;
  /** Terms this page is built to be the answer for. Also used for keywords. */
  keywords: string[];
  sections: ServiceSection[];
  /** What the client actually receives. Rendered as a checklist. */
  deliverables: string[];
  /** Who this is genuinely a fit for. */
  bestFor: string[];
  faqs: ServiceFAQ[];
  /** Slugs of related services for internal linking. */
  related: string[];
}

export const SERVICES: ServiceDefinition[] = [
  {
    slug: 'ai-seo',
    navLabel: 'AI SEO Services',
    metaTitle: 'AI SEO Services — Get Found in AI Search',
    metaDescription:
      'AI SEO services that get your business cited in ChatGPT, Perplexity and Google AI Overviews — built on evidence, not hype, with honest measurement.',
    h1: 'AI SEO Services: Getting Found in Google and AI Search',
    directAnswer:
      'AI SEO is the work of making a business visible wherever people now search — Google’s results, Google’s AI Overviews, and AI assistants such as ChatGPT and Perplexity. It combines traditional SEO with answer engine optimization and generative engine optimization. Google’s own guidance describes optimising for AI search as “still SEO”.',
    cardSummary:
      'Be found in Google, AI Overviews and AI assistants — on evidence, not hype.',
    serviceType: 'AI Search Optimization',
    keywords: [
      'ai seo services',
      'ai seo',
      'ai driven seo',
      'ai powered seo',
      'ai seo optimization',
      'ai website optimization',
      'ai search optimization',
    ],
    sections: [
      {
        heading: 'What AI SEO actually means',
        body: [
          '“AI SEO” is used to mean two very different things, and it is worth separating them before spending money. One meaning is using AI tools to produce SEO content faster. The other — the one that matters for being found — is making a business visible inside AI-generated answers as well as ordinary search results.',
          'This service is the second. It is not a content mill. Publishing large volumes of AI-written pages to chase rankings is named in Google’s spam policies as scaled content abuse, which applies “no matter how it’s created”. Doing that to a client’s site is a way to get it penalised.',
        ],
      },
      {
        heading: 'AI-driven SEO, AI-powered SEO, AI website optimization: what the labels mean',
        body: [
          'Buyers search for the same thing under several names: AI-driven SEO, AI-powered SEO, AI SEO optimization, AI website optimization. Agencies use them interchangeably and none has an agreed definition. The useful question to ask of any of them is which meaning is being sold — AI tools used to do SEO work, or optimization for visibility in AI search results.',
          'We use AI tools where they speed up analysis, such as reading large search-term reports or checking pages for questions they leave unanswered. We do not publish pages at volume, and every figure we publish states its source. What you are paying for is being found and cited, not a quantity of AI-written content.',
        ],
      },
      {
        heading: 'What the evidence supports',
        body: [
          'Much of what is sold as AI SEO has nothing behind it. What does have evidence is narrower and less glamorous — and it works because it is mostly good SEO done properly.',
        ],
        bullets: [
          'Crawl access for the AI search bots — OAI-SearchBot, Claude-SearchBot, PerplexityBot and Googlebot — checked at both robots.txt and CDN level',
          'Quotable content: the only peer-reviewed GEO study found adding statistics, quotations and cited sources raised visibility in AI answers by roughly 27–41%',
          'Self-contained answers under question-phrased headings, so a passage can be lifted and attributed out of context',
          'Consistent business details across every profile and directory, so AI systems treat the business as one entity',
          'Earned third-party mentions — one study found 72.7–81.9% of sources cited by GPT were third-party media rather than brands’ own sites',
          'Honest measurement: mention rate across repeated prompts, never a fictitious “AI ranking”',
        ],
      },
      {
        heading: 'What we will not sell you',
        body: [
          'An llms.txt file presented as an AI lever. Google says its search systems do not use it, and a study of roughly 300,000 domains found no relationship between having one and being cited.',
          'Schema markup presented as an AI-citation lever. The best available test — 1,885 pages against roughly 4,000 controls — found no benefit, and a small decline in AI Overview citations.',
          'A guaranteed AI ranking. In repeated testing, the same list of brands appeared in fewer than 1% of runs of an identical prompt. Anyone reporting a stable “position” in ChatGPT is reporting something that does not exist.',
        ],
      },
    ],
    deliverables: [
      'Crawler-access audit across robots.txt and CDN bot settings',
      'Baseline of how AI assistants currently describe your business, across a set of real buyer prompts',
      'Content restructuring for quotability — specific figures, sources and self-contained answers',
      'Entity consistency across your website, Google Business Profile and directory listings',
      'A plan for earning genuine third-party mentions',
      'Monthly mention-rate measurement with the method stated',
    ],
    bestFor: [
      'Businesses whose buyers research and compare before getting in touch',
      'Companies that already rank reasonably and want AI-answer visibility on top',
      'Anyone who has been pitched AI SEO and wants an honest read on what is real',
      'Considered-purchase and B2B services, where AI research is most common',
    ],
    faqs: [
      {
        question: 'What is AI SEO?',
        answer:
          'AI SEO is making a business visible in AI-powered search — Google’s AI Overviews and assistants like ChatGPT and Perplexity — as well as in ordinary results. It combines traditional SEO with answer engine and generative engine optimization. It should not be confused with using AI to mass-produce content, which risks a spam penalty.',
      },
      {
        question: 'Is AI SEO different from normal SEO?',
        answer:
          'Less than it is usually sold as. Google’s own guidance says optimising for its generative AI search is “still SEO”, because AI Overviews are built on the same ranking systems. AI SEO adds emphasis on quotable content, crawler access for AI bots, and what third-party sites say about you.',
      },
      {
        question: 'Can you guarantee my business appears in ChatGPT?',
        answer:
          'No, and nobody honestly can. AI answers vary from run to run — in one large test the same brand list repeated in fewer than 1% of runs. What can be improved and measured is how often you are mentioned across many relevant prompts.',
      },
      {
        question: 'Do I need an llms.txt file for AI SEO?',
        answer:
          'There is no evidence it helps. Google says its search systems don’t use it, and a study of around 300,000 domains found no relationship between having one and being cited by AI. It does no harm, but it is not worth paying for.',
      },
      {
        question: 'Will AI-generated content help my SEO?',
        answer:
          'Using AI to assist writing is fine. Publishing large volumes of AI-generated pages to manipulate rankings is named in Google’s spam policies as scaled content abuse, which applies however the content was produced. It is one of the faster ways to get a site demoted.',
      },
    ],
    related: ['geo', 'aeo', 'seo'],
  },
  {
    slug: 'google-ads-management',
    navLabel: 'Google Ads Management',
    metaTitle: 'Google Ads Management Services',
    metaDescription:
      'Managed Google Ads for home service and local businesses — phrase and exact match, real conversion tracking, and reporting on booked jobs instead of clicks.',
    h1: 'Google Ads Management for Service Businesses',
    directAnswer:
      'Google Ads management is the ongoing work of building, monitoring and refining paid search campaigns so a business pays for clicks that become customers. WeBuildPro Global runs Google Search campaigns for service businesses in the US and Canada, with conversion tracking wired to real lead actions — calls, form submissions and booked jobs.',
    cardSummary:
      'Managed Google Search campaigns built around booked jobs, not clicks.',
    serviceType: 'Google Ads Management',
    keywords: [
      'google ads management',
      'google ads management for service businesses',
      'ppc management for contractors',
      'google ads agency for home services',
      'managed google search campaigns',
      'google ads for plumbers',
      'google ads for HVAC contractors',
    ],
    sections: [
      {
        heading: 'What Google Ads management actually involves',
        body: [
          'Most of the money wasted in a Google Ads account is wasted quietly. Broad match keywords pull in searches that were never going to convert, conversion tracking counts page views as leads, and the monthly report shows impressions climbing while the phone stays silent. The account looks busy and produces nothing.',
          'Managing an account properly means deciding — continuously — which searches are worth paying for and which are not. That is keyword match type discipline, negative keyword lists that grow every week, bid strategies matched to how much data the account actually has, and tracking that fires on a real lead rather than a scroll.',
        ],
        bullets: [
          'Phrase and exact match as the default, so you pay for intent rather than adjacency',
          'Negative keyword lists built and extended from the real search terms report',
          'Conversion tracking tied to calls, form submissions and phone-number clicks',
          'Ad groups kept tight instead of campaign sprawl that splits a small budget',
          'Bid strategy chosen for the data the account has, not the data it wishes it had',
        ],
      },
      {
        heading: 'Why small budgets need different handling',
        body: [
          'A $2,000/month account cannot be run like a $50,000/month account. Smart Bidding strategies need conversion volume to learn from; starve them and they spend the budget exploring instead of converting. Spreading a small budget across six campaigns guarantees none of them gathers enough data to optimise.',
          'For smaller accounts the approach is deliberately narrow: fewer campaigns, tighter ad groups, manual control where automation has nothing to learn from, and patience through the learning period rather than daily changes that reset it.',
        ],
      },
      {
        heading: 'Reporting that answers the only question that matters',
        body: [
          'The question is not how many impressions the account served. It is how many customers it produced and what each one cost. Reporting is built around cost per lead and which keywords generated those leads, so the decision to increase, hold or cut spend is made on evidence.',
          'Where a client has call tracking available, calls are recorded and reviewed so the difference between a lead and a wrong number is visible in the numbers rather than assumed.',
        ],
      },
    ],
    deliverables: [
      'Full account build or audit and restructure of an existing account',
      'Keyword research with match-type strategy and negative keyword lists',
      'Responsive Search Ads written and tested against each other',
      'Conversion tracking implementation and verification',
      'Ongoing bid, budget and search-term management',
      'Monthly reporting on leads and cost per lead',
    ],
    bestFor: [
      'Home service businesses — plumbing, HVAC, drain, roofing, electrical',
      'Local service businesses with a defined service area',
      'Businesses already spending on ads with no reliable conversion data',
      'Owners who want the phone to ring rather than a dashboard to admire',
    ],
    faqs: [
      {
        question: 'How much should I spend on Google Ads?',
        answer:
          'Enough to generate meaningful data in your market. In competitive home-service categories, cost per click often runs $4 to $12, so a budget that produces only a handful of clicks per day will take months to reveal anything. Your free strategy call includes an honest estimate for your specific service and area.',
      },
      {
        question: 'How long before Google Ads produces leads?',
        answer:
          'Clicks start the day the campaign goes live. Reliable performance takes longer: new campaigns pass through a learning period, and the first few weeks are spent cutting wasted spend from the search terms report. Expect four to eight weeks before the account settles into a predictable cost per lead.',
      },
      {
        question: 'Do you use broad match keywords?',
        answer:
          'Not as a default. Broad match gives Google wide latitude to interpret intent, which on a small budget usually means paying for searches with no commercial value. Phrase and exact match keep spend on searches that indicate someone is actually looking to hire.',
      },
      {
        question: 'Will I own my Google Ads account?',
        answer:
          'Yes. Campaigns are built in an account you own, and access is granted at manager level rather than by sharing passwords. If the engagement ends, the account, its history and its data stay with you.',
      },
      {
        question: 'Can you fix an account another agency built?',
        answer:
          'Often, yes. The first step is an audit of structure, match types, negatives and conversion tracking. In many underperforming accounts the headline problem is that conversion tracking was never implemented correctly, which means every optimisation decision was made on incomplete data.',
      },
    ],
    related: ['meta-ads-management', 'seo', 'conversion-rate-optimization'],
  },
  {
    slug: 'seo',
    navLabel: 'SEO',
    metaTitle: 'SEO Services for Service Businesses',
    metaDescription:
      'Technical SEO, on-page optimisation and content structure for service businesses that need to be found on Google — with honest timelines, not ranking guarantees.',
    h1: 'SEO Services for Local and Service Businesses',
    directAnswer:
      'SEO is the work of making a website the clearest, most trustworthy answer to the searches your customers actually type. It covers technical health, page structure, content and local signals. For a service business, the goal is to appear when someone nearby searches for the job you do and is ready to hire.',
    cardSummary:
      'Technical, on-page and local SEO built around the searches that produce customers.',
    serviceType: 'Search Engine Optimization',
    keywords: [
      'seo services',
      'local seo for service businesses',
      'seo for contractors',
      'technical seo audit',
      'on-page seo services',
      'seo agency for small business',
    ],
    sections: [
      {
        heading: 'What SEO work consists of',
        body: [
          'SEO splits into three parts, and skipping any one of them wastes the other two. Technical SEO makes sure search engines can crawl, render and index the site at all. On-page SEO makes sure each page has an obvious, singular subject that matches a real search. Content and authority work give search engines a reason to prefer your page over the dozen others covering the same ground.',
          'A site can be technically flawless and rank for nothing because no page targets a query anyone searches. Equally, excellent content ranks for nothing if a stray configuration line keeps it out of the index. Both halves have to hold.',
        ],
        bullets: [
          'Crawlability, indexation, canonical tags and sitemap accuracy',
          'Page speed and Core Web Vitals',
          'Title tags, meta descriptions and heading hierarchy that match search intent',
          'Structured data so search engines understand what each page is',
          'Internal linking so authority reaches the pages meant to rank',
          'Local signals — Google Business Profile, service-area clarity, consistent business details',
        ],
      },
      {
        heading: 'Honest timelines, and why they matter',
        body: [
          'A new domain does not rank quickly, regardless of how well it is built. Search engines need time to crawl, index and develop confidence in a site, and that confidence builds largely through signals accumulated over months. A domain a few weeks old competing against sites that have existed for years is not losing because of poor optimisation; it is losing because of time.',
          'This is worth being direct about, because the industry is not. Anyone guaranteeing first-page rankings inside 30 or 45 days on a fresh domain is either redefining "first page" to mean a term nobody searches, or setting up a refund. Realistic SEO work on a new site is measured in months, with early movement visible in impressions and long-tail positions well before head terms shift.',
        ],
      },
      {
        heading: 'Where a small business can actually win',
        body: [
          'Competing for a two-word head term against national companies with enormous link profiles is a losing allocation of a small budget. The winnable ground is specific: service plus location, service plus customer problem, and the long questions people type when they are close to hiring.',
          'These searches have lower volume individually and far higher intent. Someone searching a precise problem in a named city is much closer to booking than someone typing a generic industry term, and the competition for those phrases is a fraction as fierce.',
        ],
      },
    ],
    deliverables: [
      'Technical audit covering indexation, speed, structure and structured data',
      'Keyword research mapped to pages, with realistic difficulty assessment',
      'On-page optimisation of titles, headings, metadata and internal links',
      'Schema markup implementation and validation',
      'Content recommendations or production for target queries',
      'Google Business Profile setup and optimisation for local visibility',
    ],
    bestFor: [
      'Service businesses that rely on being found locally',
      'Sites that get traffic but no enquiries',
      'Businesses whose site was built without SEO considered',
      'Owners who want realistic timelines instead of ranking promises',
    ],
    faqs: [
      {
        question: 'How long does SEO take to work?',
        answer:
          'On an established domain, meaningful movement typically appears within two to four months. On a brand-new domain, expect longer — search engines need time to crawl, index and build confidence in a site. Early progress shows up as rising impressions and long-tail positions before competitive terms move.',
      },
      {
        question: 'Can you guarantee first-page rankings?',
        answer:
          'No, and treat any agency that does with suspicion. Rankings depend on competitors, search engine updates and domain history, none of which any agency controls. What can be committed to is the work itself: the technical fixes, the page structure, the content and the reporting that shows what is moving.',
      },
      {
        question: 'What is the difference between SEO and local SEO?',
        answer:
          'Local SEO targets searches with geographic intent and the map results that appear for them. It leans on Google Business Profile, consistent business information, location-relevant pages and reviews. For a business serving a defined area, local SEO is usually where the return is highest.',
      },
      {
        question: 'Do I need new content, or can you optimise what I have?',
        answer:
          'Both are usually involved. Existing pages often rank poorly because their subject is vague rather than because they are badly written, and tightening them is the faster win. New content is needed where a search you want to win has no page on the site addressing it.',
      },
      {
        question: 'Does SEO still matter if AI is answering questions?',
        answer:
          'Yes, and the foundations overlap heavily. AI answer engines draw on content they can crawl, parse and trust — the same crawlability, structure and clarity that traditional SEO builds. Optimising for AI visibility sits on top of SEO fundamentals rather than replacing them.',
      },
    ],
    related: ['aeo', 'geo', 'google-ads-management'],
  },
  {
    slug: 'aeo',
    navLabel: 'Answer Engine Optimization',
    metaTitle: 'Answer Engine Optimization (AEO)',
    metaDescription:
      'AEO services that structure your content so Google AI Overviews, featured snippets and voice assistants quote your business as the direct answer.',
    h1: 'Answer Engine Optimization (AEO) Services',
    directAnswer:
      'Answer Engine Optimization is the practice of structuring content so search engines and AI assistants can extract a direct answer from it and attribute that answer to your business. Where SEO competes for a position in a list of links, AEO competes to be the response itself — in featured snippets, AI Overviews and voice results.',
    cardSummary:
      'Structure your content to be quoted as the answer, not listed as a link.',
    serviceType: 'Answer Engine Optimization',
    keywords: [
      'answer engine optimization',
      'aeo services',
      'featured snippet optimization',
      'google ai overviews optimization',
      'voice search optimization',
      'how to rank in ai overviews',
    ],
    sections: [
      {
        heading: 'Why position one stopped being the finish line',
        body: [
          'Search results increasingly answer the question on the results page. A featured snippet, an AI Overview or a voice assistant reads a passage aloud and the user never clicks anything. The link that ranks first still exists; it just gets passed over because the answer arrived first.',
          'That changes what a page has to do. It is no longer enough to be relevant enough to rank — the page has to contain a passage that can be lifted cleanly and stands on its own out of context. Most business websites contain nothing of the kind, because marketing copy is written to persuade across several paragraphs rather than to answer in one.',
        ],
      },
      {
        heading: 'What makes a passage extractable',
        body: [
          'Answer engines favour a specific shape: a question-phrased heading, immediately followed by a self-contained answer of roughly 40 to 60 words that makes sense with nothing around it. No "as we mentioned above", no build-up, no pronoun pointing at a previous paragraph.',
          'Beyond the passage itself, structure carries weight. Clean heading hierarchy, genuine FAQ markup, comparison tables, numbered steps for processes, and visible dates all make content easier to parse and attribute.',
        ],
        bullets: [
          'Headings written as the question a customer would actually ask',
          'A standalone 40-60 word answer directly beneath each one',
          'FAQPage schema that matches the visible questions on the page',
          'Tables for comparisons, numbered lists for sequences',
          'Specific figures with dates and sources rather than vague claims',
          'Clean H2 to H3 hierarchy with one subject per section',
        ],
      },
      {
        heading: 'AEO and SEO are not alternatives',
        body: [
          'AEO does not replace SEO; it sits on top of it. A page has to be crawlable, indexed and relevant before it can be considered as an answer at all. The technical foundation is the same foundation.',
          'What AEO adds is a layer of formatting discipline. The same facts, reorganised so a machine can lift them, become eligible for placements a conventionally written page cannot reach.',
        ],
      },
    ],
    deliverables: [
      'Audit of existing content for extractable answer passages',
      'Question research — the phrasings customers actually use',
      'Content restructuring into question-and-answer format',
      'FAQPage, HowTo and Speakable schema implementation',
      'Comparison tables and structured lists where they fit the content',
      'Tracking of featured snippet and AI Overview appearances',
    ],
    bestFor: [
      'Businesses whose customers research before buying',
      'Sites with existing content that gets impressions but few clicks',
      'Service businesses answering the same questions on every sales call',
      'Anyone whose category is increasingly answered by AI Overviews',
    ],
    faqs: [
      {
        question: 'What is the difference between AEO and SEO?',
        answer:
          'SEO works to place a page highly in a list of results. AEO works to have the page quoted as the answer itself — in a featured snippet, an AI Overview or a voice response. They share a technical foundation, but AEO adds formatting discipline aimed at making passages extractable.',
      },
      {
        question: 'How do I get into Google AI Overviews?',
        answer:
          'There is no submission process. AI Overviews draw on content Google can already crawl and trust, favouring pages that answer a question directly, clearly and early. Practically, that means question-phrased headings, concise standalone answers, supporting structured data and genuine topical depth.',
      },
      {
        question: 'Does AEO work for local service businesses?',
        answer:
          'Often particularly well. Customers ask practical questions — what a job costs, how long it takes, whether something is urgent — and these are exactly the queries answer engines surface. A page that answers them plainly can be cited where a generic services page never would be.',
      },
      {
        question: 'How is AEO measured?',
        answer:
          'Through featured snippet appearances, impressions on question-shaped queries in Search Console, referral traffic from AI platforms, and periodic manual checks of how a set of target questions is answered across assistants. It is less precise than rank tracking, which is worth being upfront about.',
      },
      {
        question: 'Will being quoted cost me clicks?',
        answer:
          'Sometimes a user gets their answer and does not click. But being named as the source of the answer carries its own value, and the questions worth targeting are the ones where the answer naturally leads to contact — pricing, suitability, what to do next.',
      },
    ],
    related: ['ai-seo', 'geo', 'seo'],
  },
  {
    slug: 'geo',
    navLabel: 'Generative Engine Optimization',
    metaTitle: 'Generative Engine Optimization (GEO)',
    metaDescription:
      'GEO services that build the entity clarity, authority signals and content depth AI engines rely on when recommending a business in ChatGPT, Perplexity and Gemini.',
    h1: 'Generative Engine Optimization (GEO) Services',
    directAnswer:
      'Generative Engine Optimization is the practice of making a business recognisable and recommendable to AI systems like ChatGPT, Perplexity and Gemini. Where AEO targets a single extracted answer, GEO targets the broader judgement an AI makes when a user asks it to recommend or compare options in a category.',
    cardSummary:
      'Make AI assistants recognise your business and recommend it by name.',
    serviceType: 'Generative Engine Optimization',
    keywords: [
      'generative engine optimization',
      'geo services',
      'ai search optimization',
      'how to get recommended by chatgpt',
      'perplexity optimization',
      'ai visibility for business',
    ],
    sections: [
      {
        heading: 'A different kind of search behaviour',
        body: [
          'A growing share of buying research now starts with a conversation rather than a query. Someone asks an assistant to recommend a good agency for a particular need, or to compare two providers, and acts on the handful of names that come back. There is no results page to rank on — there is a shortlist, and either you are on it or you are not.',
          'What determines inclusion is different from what determines ranking. The model has to recognise the business as a distinct entity, associate it confidently with a category, and find enough corroborating information to mention it without hedging.',
        ],
      },
      {
        heading: 'What GEO work actually changes',
        body: [
          'The foundation is entity clarity: the business described consistently, in the same terms, everywhere it appears. Organization schema that states plainly what the company is and does. A category definition on the site that explains the space in the language a user would use when asking about it.',
          'The second half is corroboration. Models weigh consistency across sources, so matching details across directories, profiles and any coverage of the business matter more here than they do for conventional ranking.',
        ],
        bullets: [
          'Organization and Service schema with explicit service taxonomy',
          'A machine-readable llms.txt summary of what the business does',
          'Plain-language category definitions on the site',
          'Consistent naming, description and contact details across every profile',
          'Decision-criteria content — who the business suits and who it does not',
          'Honest comparison content positioning the business against alternatives',
        ],
      },
      {
        heading: 'Why stating what you are not helps',
        body: [
          'Counter-intuitively, documenting the cases a business is a poor fit for improves how reliably AI systems recommend it. Clear boundaries help a model distinguish between categories, which makes it more confident naming the business where it does fit.',
          'A site claiming to serve everyone gives a model nothing to match against. A site that says plainly which problems it solves and which it does not is far easier to place correctly.',
        ],
      },
    ],
    deliverables: [
      'Entity audit — how consistently the business is described across the web',
      'Organization, Service and WebSite schema implementation',
      'llms.txt authored for AI crawler consumption',
      'Category definition and decision-criteria content',
      'Comparison content against realistic alternatives',
      'Baseline and periodic testing of how AI assistants describe the business',
    ],
    bestFor: [
      'Businesses in categories buyers research before contacting anyone',
      'Companies with an inconsistent or thin presence across the web',
      'B2B and considered-purchase services',
      'Anyone wanting to know how AI assistants currently describe them',
    ],
    faqs: [
      {
        question: 'What is the difference between GEO and AEO?',
        answer:
          'AEO targets a specific extracted answer to a specific question — a featured snippet or a quoted passage. GEO targets the broader judgement an AI makes when recommending or comparing options in a category. AEO is about being quoted; GEO is about being recommended.',
      },
      {
        question: 'Can you control what ChatGPT says about my business?',
        answer:
          'No, and nobody can. What you can influence is the information available to be drawn on: accurate structured data, consistent descriptions across sources, and content that states plainly what the business does. Models synthesise from what exists, so improving what exists is the available lever.',
      },
      {
        question: 'How do I know if GEO is working?',
        answer:
          'By testing. A set of relevant prompts is run across the major assistants at a baseline and re-run periodically, recording whether the business is named, how it is described and whether that description is accurate. It is a sampling method rather than a precise metric, which is worth stating plainly.',
      },
      {
        question: 'Is GEO worth doing for a small local business?',
        answer:
          'It depends on how customers find you. If most arrive through local map searches or referrals, local SEO deserves the budget first. If customers research providers before reaching out, GEO is increasingly where that research happens.',
      },
      {
        question: 'Do AI crawlers need to be allowed on my site?',
        answer:
          'Yes. If robots.txt or a network-level bot setting blocks GPTBot, ClaudeBot, PerplexityBot or similar crawlers, your content cannot inform those systems. Checking and correcting crawler access is one of the first things worth verifying.',
      },
    ],
    related: ['ai-seo', 'aeo', 'seo'],
  },
  {
    slug: 'meta-ads-management',
    navLabel: 'Meta Ads Management',
    metaTitle: 'Meta Ads Management Services',
    metaDescription:
      'Managed Facebook and Instagram advertising for service businesses — creative built for the feed, correct pixel tracking, and reporting on leads rather than reach.',
    h1: 'Meta Ads Management for Facebook and Instagram',
    directAnswer:
      'Meta Ads management covers planning, building and optimising paid campaigns across Facebook and Instagram. Unlike search advertising, which reaches people already looking, Meta advertising places an offer in front of people who were not searching — which makes creative quality and audience definition the deciding factors.',
    cardSummary:
      'Facebook and Instagram campaigns where the creative does the heavy lifting.',
    serviceType: 'Meta Ads Management',
    keywords: [
      'meta ads management',
      'facebook ads management',
      'instagram ads agency',
      'facebook ads for local business',
      'meta ads for service businesses',
      'social media advertising management',
    ],
    sections: [
      {
        heading: 'Interruption, not intent',
        body: [
          'Google advertising answers demand that already exists — someone typed the problem. Meta advertising creates demand by interrupting someone who was scrolling. That single difference determines everything about how the campaign should be built.',
          'Because there is no search query to signal intent, the creative has to do the work: stop the scroll, communicate the offer and make the next step obvious, usually within the first couple of seconds. A mediocre ad with perfect targeting will underperform a strong ad with adequate targeting, almost every time.',
        ],
      },
      {
        heading: 'What usually goes wrong',
        body: [
          'Three failures account for most wasted Meta spend. The pixel was never installed properly, so the platform optimises blind. The campaign is edited constantly, resetting the learning phase before it can stabilise. Or the creative is a repurposed brochure image that looks like an advert and gets scrolled past accordingly.',
          'There is also a budget floor worth naming. Below a certain spend the platform cannot gather enough conversion data to optimise, and the account stays permanently in a learning state — burning budget on exploration that never resolves.',
        ],
        bullets: [
          'Pixel and Conversions API installed and verified against real events',
          'Campaign structure that avoids repeated learning-phase resets',
          'Creative built for the feed — native, fast, mobile-first',
          'Several creative angles tested rather than one asset refreshed',
          'Audience definition appropriate to the budget available',
          'Reporting on cost per lead, not reach and impressions',
        ],
      },
      {
        heading: 'Where Meta fits alongside search',
        body: [
          'For most service businesses, search captures existing demand and Meta builds awareness and retargets. Running both without a plan splits the budget and muddies attribution. Running them deliberately means each channel does what it is actually good at.',
          'Where a budget only supports one channel properly, the honest recommendation is usually to do one well rather than both badly — and which one depends on whether people already search for what you sell.',
        ],
      },
    ],
    deliverables: [
      'Pixel and Conversions API setup and event verification',
      'Campaign and ad set structure built for the available budget',
      'Audience research, creation and exclusion management',
      'Creative direction and ad copywriting',
      'Ongoing testing across creative angles and audiences',
      'Reporting on leads and cost per lead',
    ],
    bestFor: [
      'Businesses with a visually demonstrable result — before and after work',
      'Offers that benefit from explanation rather than pure search capture',
      'Businesses already running search ads and ready to add awareness',
      'Local businesses with a defined geographic radius',
    ],
    faqs: [
      {
        question: 'Do Facebook ads still work for local service businesses?',
        answer:
          'Yes, when the creative suits the platform. Meta reaches people who were not searching, which makes it effective for demonstrable results and for staying visible between purchase moments. It is generally weaker than search for capturing someone who needs the job done today.',
      },
      {
        question: 'What is the minimum budget for Meta ads?',
        answer:
          'Below roughly $1,000 per month, most accounts struggle to leave the learning phase — the platform cannot gather enough conversion data to optimise, so performance stays unstable. A smaller budget is usually better spent on search, where intent compensates for volume.',
      },
      {
        question: 'Why do my Meta ads get clicks but no leads?',
        answer:
          'Usually a mismatch between the ad and the landing page, or tracking that fires on the wrong event. If the ad promises one thing and the page delivers another, people leave. If the pixel counts page views as conversions, the platform optimises toward people who bounce.',
      },
      {
        question: 'Who produces the creative?',
        answer:
          'Direction, copy and structure are handled as part of management. Raw footage or photography of actual work is usually supplied by the client, because authentic material consistently outperforms stock imagery — particularly for trades where real before-and-after work is the proof.',
      },
      {
        question: 'Should I run Google and Meta at the same time?',
        answer:
          'If the budget supports both properly, yes — they do different jobs. If it does not, running one channel well beats splitting spend across two that each receive too little to optimise. Which one comes first depends on whether people already search for your service.',
      },
    ],
    related: ['google-ads-management', 'conversion-rate-optimization', 'web-design-development'],
  },
  {
    slug: 'web-design-development',
    navLabel: 'Website Design & Development',
    metaTitle: 'Web Design & Development Services',
    metaDescription:
      'Fast, conversion-focused websites for service businesses — built mobile-first, structured for SEO and AI visibility, and wired for conversion tracking from day one.',
    h1: 'Website Design and Development for Service Businesses',
    directAnswer:
      'A service business website has one job: turn a visitor into an enquiry. That requires fast load times, an obvious next step on every screen, proof that the business is real and competent, and a structure search engines and AI systems can read. Design that looks impressive but buries the phone number fails at the only task that matters.',
    cardSummary:
      'Fast, mobile-first sites built to convert visitors into enquiries.',
    serviceType: 'Web Design and Development',
    keywords: [
      'website design for service businesses',
      'web development agency',
      'conversion focused web design',
      'website design for contractors',
      'small business website development',
      'high converting website design',
    ],
    sections: [
      {
        heading: 'What a converting site gets right',
        body: [
          'Most visitors arrive on a phone, often with a problem that needs solving soon. They decide within seconds whether the business looks capable and whether contacting it is easy. Everything else — animation, cleverness, extended brand storytelling — is secondary to those two judgements.',
          'In practice that means the service, the area covered and the way to get in touch are visible without scrolling. It means the page loads before patience runs out. And it means proof — real work, real detail — rather than stock photography and adjectives.',
        ],
        bullets: [
          'Load performance treated as a conversion factor, not a technical nicety',
          'Mobile-first layout, because that is where the traffic is',
          'A clear next step visible in every viewport',
          'Click-to-call that works on the first tap',
          'Forms short enough to finish one-handed',
          'Conversion tracking wired in at build time rather than retrofitted',
        ],
      },
      {
        heading: 'Built so search and AI can read it',
        body: [
          'A site built without search structure needs rebuilding later, which costs more than doing it correctly at the start. Clean heading hierarchy, server-rendered content, accurate structured data, a real sitemap and canonical URLs cost nothing extra during the build and are expensive to bolt on afterwards.',
          'The same structure that helps search engines helps AI systems parse and cite the site, so building it properly once serves both.',
        ],
      },
      {
        heading: 'Tracking from day one',
        body: [
          'A site launched without conversion tracking produces no usable data, and every marketing decision made afterwards is guesswork. This is the single most common gap found when auditing an existing site: analytics present, conversion events either missing or firing on the wrong action.',
          'Form submissions, phone clicks and call events are configured and verified before launch, so the first week of traffic is measurable.',
        ],
      },
    ],
    deliverables: [
      'Custom design built around the enquiry path, not a stock template',
      'Mobile-first responsive build with performance budgets',
      'SEO structure — headings, metadata, schema, sitemap, canonicals',
      'Lead capture with reliable form delivery and spam handling',
      'Analytics and conversion tracking configured and verified',
      'Launch, DNS and hosting setup handled end to end',
    ],
    bestFor: [
      'Service businesses with no site, or a site that does not generate enquiries',
      'Businesses about to run paid ads that need a page worth sending traffic to',
      'Companies on a slow or unmaintainable website builder',
      'Owners who want the site and the marketing built as one system',
    ],
    faqs: [
      {
        question: 'How long does a website take to build?',
        answer:
          'A focused service-business site typically takes one to three weeks from kickoff, depending on scope and how quickly content and photography are supplied. Larger builds with many service pages take longer. The timeline is agreed in writing before work starts.',
      },
      {
        question: 'Will I be able to edit the site myself?',
        answer:
          'Content you are likely to change — service descriptions, articles, contact details — is set up to be editable without touching code. Structural changes are better handled as part of ongoing support, since layout edits are where self-built changes most often break a page.',
      },
      {
        question: 'Do you redesign existing websites?',
        answer:
          'Yes. The first step is determining whether the existing site is worth optimising or whether rebuilding is cheaper. A site that is fundamentally sound but poorly structured is often fixable; one built on a slow platform with no path to improvement usually is not.',
      },
      {
        question: 'What does a website cost?',
        answer:
          'Pricing depends on scope — number of pages, functionality, and whether content and photography exist. Every engagement is quoted after a free strategy call, with scope and milestones agreed in writing before work begins. No package pricing is published because the situations genuinely differ.',
      },
      {
        question: 'Do I own the website when it is finished?',
        answer:
          'Yes. The site, the domain and the hosting account are yours. There is no proprietary platform holding the site hostage, and no lock-in that makes leaving expensive.',
      },
    ],
    related: ['conversion-rate-optimization', 'seo', 'google-ads-management'],
  },
  {
    slug: 'conversion-rate-optimization',
    navLabel: 'Conversion Rate Optimization',
    metaTitle: 'Conversion Rate Optimization (CRO)',
    metaDescription:
      'CRO for service businesses — find why visitors leave without enquiring and fix it, so existing traffic produces more leads without increasing ad spend.',
    h1: 'Conversion Rate Optimization for Service Businesses',
    directAnswer:
      'Conversion rate optimisation is the work of increasing the share of visitors who take action — calling, submitting a form, booking. For a business already paying for traffic, improving conversion rate is usually cheaper than buying more visitors, because every improvement applies to traffic already being paid for.',
    cardSummary:
      'Get more enquiries from the traffic you already pay for.',
    serviceType: 'Conversion Rate Optimization',
    keywords: [
      'conversion rate optimization',
      'cro services',
      'landing page optimization',
      'improve website conversion rate',
      'why is my website not converting',
      'cro for service businesses',
    ],
    sections: [
      {
        heading: 'The cheapest traffic is traffic you already have',
        body: [
          'If a site converts two percent of visitors and the conversion rate moves to three, lead volume rises by half with no additional ad spend. The same improvement applied to organic traffic compounds, because nothing has to be bought twice.',
          'This is why CRO usually deserves attention before a budget increase. Scaling spend on a page that does not convert simply buys more of the same disappointment at a larger scale.',
        ],
      },
      {
        heading: 'Where enquiries are usually lost',
        body: [
          'The common failures are mundane. Load time long enough that mobile visitors leave before seeing anything. A form asking for more information than the stage of the relationship justifies. No phone number visible where someone has decided to call. No indication that the business serves the visitor\'s area. No evidence the business has done the job before.',
          'None of these require redesign to fix. They require finding the specific point where people are dropping out and removing the obstacle at that point.',
        ],
        bullets: [
          'Load performance on real mobile connections, not desktop fibre',
          'Form length and field count relative to intent',
          'Click-to-call visibility and function',
          'Service area stated clearly enough to pass the "do they cover me" test',
          'Proof of actual completed work',
          'Message match between the ad clicked and the page it lands on',
        ],
      },
      {
        heading: 'Evidence over opinion',
        body: [
          'Design opinions are plentiful and mostly unfalsifiable. CRO works from evidence: what analytics show about where people leave, what session behaviour reveals about confusion, and what changes actually move the conversion rate when tested.',
          'On lower-traffic sites, formal A/B testing often cannot reach significance in reasonable time. In that case the honest approach is sequential improvement against known best practice with before-and-after measurement, stated as such rather than dressed up as a controlled experiment.',
        ],
      },
    ],
    deliverables: [
      'Conversion audit identifying where and why visitors drop out',
      'Analytics and event tracking review and correction',
      'Page speed and mobile experience assessment',
      'Form, call-to-action and lead-path improvements',
      'Message-match review between ad campaigns and landing pages',
      'Before-and-after measurement of each change',
    ],
    bestFor: [
      'Businesses paying for traffic that is not converting',
      'Sites with reasonable visitor numbers and few enquiries',
      'Anyone about to increase ad spend on an unproven page',
      'Businesses that suspect their forms or tracking are broken',
    ],
    faqs: [
      {
        question: 'What is a good conversion rate for a service business website?',
        answer:
          'It varies widely by industry and traffic source. Local service sites receiving high-intent search traffic often convert between two and five percent, while broader awareness traffic converts far lower. The useful comparison is your site against itself over time, not against a published average.',
      },
      {
        question: 'Why is my website getting traffic but no enquiries?',
        answer:
          'The common causes are slow mobile load times, an unclear next step, forms that ask too much too early, no visible service area, or a mismatch between what an ad promised and what the page delivers. An audit identifies which apply rather than guessing.',
      },
      {
        question: 'Do I need a full redesign to improve conversions?',
        answer:
          'Usually not. Most conversion problems trace to a handful of specific obstacles that can be fixed without rebuilding. A redesign is warranted when the underlying platform is too slow or too rigid to improve, which is a different problem.',
      },
      {
        question: 'How long does CRO take to show results?',
        answer:
          'Technical fixes such as load speed and broken tracking show up almost immediately. Changes to layout and messaging need enough traffic to judge fairly — typically a few weeks on a site with steady visitor numbers, longer on a quieter one.',
      },
      {
        question: 'Can you run A/B tests on my site?',
        answer:
          'Where traffic supports it, yes. Below a certain volume a test cannot reach statistical significance in a useful timeframe, and claiming otherwise would be dishonest. On lower-traffic sites the approach is sequential improvement with clear before-and-after measurement.',
      },
    ],
    related: ['web-design-development', 'google-ads-management', 'meta-ads-management'],
  },
];

export function getService(slug: string): ServiceDefinition | undefined {
  return SERVICES.find((service) => service.slug === slug);
}

export function getAllServiceSlugs(): string[] {
  return SERVICES.map((service) => service.slug);
}
